import React, { useState } from 'react';
import { Paper, Typography, Box, Button, Grid, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';

const steps = ['profile', 'goals', 'budget', 'result'] as const;
type StepKey = (typeof steps)[number];

type ProfileAnswers = {
  ageRange: string;
  familyStatus: string;
  dependents: string;
  mainGoal: string;
  budget: string;
};

const InsuranceNeedsWizard: React.FC = () => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState<StepKey>('profile');
  const [answers, setAnswers] = useState<ProfileAnswers>({
    ageRange: '',
    familyStatus: '',
    dependents: '',
    mainGoal: '',
    budget: '',
  });

  const handleSelect = (field: keyof ProfileAnswers, value: string) => {
    setAnswers((prev) => ({ ...prev, [field]: value }));
  };

  const next = () => {
    const currentIndex = steps.indexOf(activeStep);
    if (currentIndex < steps.length - 1) {
      setActiveStep(steps[currentIndex + 1]);
    }
  };

  const back = () => {
    const currentIndex = steps.indexOf(activeStep);
    if (currentIndex > 0) {
      setActiveStep(steps[currentIndex - 1]);
    }
  };

  const getRecommendation = () => {
    const { ageRange, familyStatus, dependents, mainGoal, budget } = answers;

    if (mainGoal === 'incomeProtection' || familyStatus === 'withFamily' || dependents === 'yes') {
      return t('wizard.recommend.familyProtection');
    }

    if (mainGoal === 'mortgageProtection') {
      return t('wizard.recommend.mortgageProtection');
    }

    if (mainGoal === 'criticalIllness') {
      return t('wizard.recommend.criticalIllness');
    }

    if (budget === 'limited') {
      return t('wizard.recommend.budgetFriendly');
    }

    if (ageRange === '55plus') {
      return t('wizard.recommend.seniorFocus');
    }

    return t('wizard.recommend.general');
  };

  const renderProfileStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('wizard.profile.title')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>
            {t('wizard.profile.ageRange')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label={t('wizard.profile.under35')}
              color={answers.ageRange === 'under35' ? 'primary' : 'default'}
              onClick={() => handleSelect('ageRange', 'under35')}
            />
            <Chip
              label={t('wizard.profile.35to55')}
              color={answers.ageRange === '35to55' ? 'primary' : 'default'}
              onClick={() => handleSelect('ageRange', '35to55')}
            />
            <Chip
              label={t('wizard.profile.55plus')}
              color={answers.ageRange === '55plus' ? 'primary' : 'default'}
              onClick={() => handleSelect('ageRange', '55plus')}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>
            {t('wizard.profile.familyStatus')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label={t('wizard.profile.single')}
              color={answers.familyStatus === 'single' ? 'primary' : 'default'}
              onClick={() => handleSelect('familyStatus', 'single')}
            />
            <Chip
              label={t('wizard.profile.withFamily')}
              color={answers.familyStatus === 'withFamily' ? 'primary' : 'default'}
              onClick={() => handleSelect('familyStatus', 'withFamily')}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>
            {t('wizard.profile.dependents')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label={t('wizard.common.yes')}
              color={answers.dependents === 'yes' ? 'primary' : 'default'}
              onClick={() => handleSelect('dependents', 'yes')}
            />
            <Chip
              label={t('wizard.common.no')}
              color={answers.dependents === 'no' ? 'primary' : 'default'}
              onClick={() => handleSelect('dependents', 'no')}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  const renderGoalsStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('wizard.goals.title')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Chip
          label={t('wizard.goals.incomeProtection')}
          color={answers.mainGoal === 'incomeProtection' ? 'primary' : 'default'}
          onClick={() => handleSelect('mainGoal', 'incomeProtection')}
        />
        <Chip
          label={t('wizard.goals.mortgageProtection')}
          color={answers.mainGoal === 'mortgageProtection' ? 'primary' : 'default'}
          onClick={() => handleSelect('mainGoal', 'mortgageProtection')}
        />
        <Chip
          label={t('wizard.goals.criticalIllness')}
          color={answers.mainGoal === 'criticalIllness' ? 'primary' : 'default'}
          onClick={() => handleSelect('mainGoal', 'criticalIllness')}
        />
      </Box>
    </Box>
  );

  const renderBudgetStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('wizard.budget.title')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Chip
          label={t('wizard.budget.limited')}
          color={answers.budget === 'limited' ? 'primary' : 'default'}
          onClick={() => handleSelect('budget', 'limited')}
        />
        <Chip
          label={t('wizard.budget.medium')}
          color={answers.budget === 'medium' ? 'primary' : 'default'}
          onClick={() => handleSelect('budget', 'medium')}
        />
        <Chip
          label={t('wizard.budget.flexible')}
          color={answers.budget === 'flexible' ? 'primary' : 'default'}
          onClick={() => handleSelect('budget', 'flexible')}
        />
      </Box>
    </Box>
  );

  const renderResultStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('wizard.result.title')}
      </Typography>
      <Typography variant="body1" paragraph>
        {getRecommendation()}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('wizard.result.disclaimer')}
      </Typography>
    </Box>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 'profile':
        return renderProfileStep();
      case 'goals':
        return renderGoalsStep();
      case 'budget':
        return renderBudgetStep();
      case 'result':
        return renderResultStep();
      default:
        return null;
    }
  };

  return (
    <Paper elevation={1} sx={{ p: 3, mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        {t('wizard.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {t('wizard.subtitle')}
      </Typography>

      {renderStepContent()}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button onClick={back} disabled={activeStep === 'profile'}>
          {t('wizard.actions.back')}
        </Button>
        {activeStep !== 'result' ? (
          <Button variant="contained" color="primary" onClick={next}>
            {t('wizard.actions.next')}
          </Button>
        ) : (
          <Button variant="contained" color="primary" onClick={() => setActiveStep('profile')}>
            {t('wizard.actions.restart')}
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default InsuranceNeedsWizard;
